<template>
  <m-dialog
    :open="modelValue"
    v-bind="{ ...$attrs, ...$props }"
    @close="handleDismiss"
    class="dialog">
    <m-dialog-header v-if="shouldDisplayHeader">
      <div class="text-left">
        <!-- @slot The header of the dialog -->
        <slot name="header"></slot>
      </div>

      <!-- Stopped: m-dialog would re-emit it as `close`, which persistent dialogs ignore -->
      <m-dialog-close
        v-if="shouldDisplayCloseButton"
        class="close-button"
        @click.stop="handleCancel"></m-dialog-close>
    </m-dialog-header>
    <m-dialog-body :class="{ 'allow-content-overflow': allowContentOverflow }">
      <!-- @slot The body of the dialog -->
      <slot name="body"></slot>
    </m-dialog-body>
    <m-dialog-footer v-if="$slots.footer">
      <!-- @slot The footer of the dialog -->
      <slot name="footer"></slot>
    </m-dialog-footer>
    <m-dialog-actions v-if="$slots.actions">
      <!-- @slot The actions for the dialog -->
      <slot name="actions" :handle-cancel="handleCancel"></slot>
    </m-dialog-actions>
  </m-dialog>
</template>

<script>
/**
 *
 * Dialog Base that can be used in specific dialog components e.g. AccountCreationErrorsDialog or DuplicateEmailErrorDialog
 *
 * Notice the convention of the dialog display data properties in the example code.  Should be a "dialogs" object with
 * the key being the name of the component sans "dialog" and a boolean value used for the underlying m-dialog component's "open" prop
 * @displayName Dialog Base
 */
export default {
  name: 'DialogBase',
  props: {
    /**
     * @model
     * Whether dialog is open or not
     */
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * If set false, header - including close button - is hidden
     */
    shouldDisplayHeader: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Dialog does not close when the outer overlay is clicked
     */
    isPersistent: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * When true, input is no longer emitted and a custom-close event is emitted
     */
    hasCustomCloseLogic: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Allows for display of the upper right close button
     */
    shouldDisplayCloseButton: {
      type: Boolean,
      required: false,
      default: true
    },
    /**
     * Lets body content (e.g. dropdown menus) render outside the dialog bounds instead of being
     * clipped. Disables body scrolling, so only use it on dialogs with short content.
     */
    allowContentOverflow: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  mounted() {
    if (this.allowContentOverflow) {
      // The native modal <dialog> lives in m-dialog's shadow root and gets overflow: auto from the UA stylesheet.
      this.util.appendStyleToShadowDomEl(this.$el, 'dialog', 'overflow: visible;');
    }
  },
  methods: {
    /**
     * Handle Escape or a backdrop click
     * @private
     */
    handleDismiss() {
      if (this.isPersistent) {
        return;
      }

      this.handleCancel();
    },
    /**
     * Handle cancel event
     * @private
     */
    handleCancel() {
      if (this.hasCustomCloseLogic) {
        /**
         * Custom close event to handle the dialog closing from the parent in a way other than the v-model binding
         * @event custom-close
         */
        this.$emit('custom-close');
      } else {
        /**
         * Emits input event with a value of false
         * Used by the parent to close the dialog
         * @property {Boolean} - Always false to hide dialog
         */
        this.$emit('update:model-value', false);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
:root {
  m-dialog {
    --margin: 100px;
    @media (max-width: $tabletBreakpoint) {
      --margin: 24px;
    }
  }
}
.allow-content-overflow {
  overflow: visible;
}
.close-button {
  position: absolute;
  right: 0;
  top: 10px;
  margin: 16px;
  cursor: pointer;
}
</style>
